import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import useHasAccessToSummarizationAndKeywords from '../../../hooks/useHasAccessToSummarizationAndKeywords';
import { updateTranscriptionSummary } from '../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice';
import type { TranscriptionSummaryWebSocketMessage } from '../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice';

const REAL_TIME_DATA_TYPE = 'call_data.transcribed_summary.openai.real_time';

/**
 * Custom hook that handles adding a callback to the Hermes websocket to handle
 * transcription summaries. The messages only come through when subscribed by
 * tel://phone_number.
 */
const useHandleTranscriptionSummaries = () => {
  const Hermes = window?.__RSOS?.hermes?.instance;

  const dispatch = useDispatch();

  const hasAccessToSummarizationAndKeywords =
    useHasAccessToSummarizationAndKeywords();

  const handleRealTimeSummarization = useCallback(
    (msg: any) => {
      if (msg?.type === REAL_TIME_DATA_TYPE) {
        const message = msg as TranscriptionSummaryWebSocketMessage;
        // Extract the callerID from the topic
        const parts = `${message?.topic}`.split('tel://');
        let callerId = '';
        if (parts.length > 1) {
          callerId = parts[1];
        }

        const summaryPayload = message?.body;
        if (summaryPayload) {
          // Add default values to summaries
          const normalizedSummary = {
            ...summaryPayload,
            summary: summaryPayload.summary || '',
            keywords: summaryPayload.keywords || [],
          };

          dispatch(
            updateTranscriptionSummary({
              callerId,
              summary: normalizedSummary,
              keywords: normalizedSummary.keywords,
            }),
          );
        }
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (Hermes) {
      Hermes.addOnMessageCallback({
        ...(hasAccessToSummarizationAndKeywords
          ? {
              handleRealTimeSummarization: handleRealTimeSummarization,
            }
          : {}),
      });
    }
  }, [
    Hermes,
    handleRealTimeSummarization,
    hasAccessToSummarizationAndKeywords,
  ]);

  return null;
};

export default useHandleTranscriptionSummaries;
